import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  UserCheck, 
  ClipboardCheck, 
  FolderKanban, 
  Settings, 
  DollarSign,
  Database,
  Menu,
  X,
  LogOut
} from 'lucide-react';
import skillsbridgeLogo from '../img/skillsbridge.png';
import './astyles/AdminLayout.css';
import { doSignOut } from '../../Firebase/auth';

export default function AdminLayout() {
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await doSignOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Update active view based on current route
  useEffect(() => {
    if (location.pathname === '/admin') setActiveView('dashboard');
    else if (location.pathname.includes('/admin/manage-records/volunteers')) setActiveView('volunteer-approvals');
    else if (location.pathname.includes('/admin/task-approvals')) setActiveView('task-approvals');
    else if (location.pathname.includes('/admin/manage-records/projects')) setActiveView('manage-projects');
    else if (location.pathname.includes('/admin/manage-skills')) setActiveView('manage-skills');
    else if (location.pathname.includes('/admin/manage-records/donations')) setActiveView('manage-donations');
    else if (location.pathname.includes('/admin/manage-records')) setActiveView('manage-records');
  }, [location]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`admin-container ${sidebarCollapsed && !isMobile ? 'collapsed' : ''}`}>
      {/* Header with logo for mobile */}
      {isMobile && (
        <header className="mobile-header">
          <Link to="/" className="logo-link">
            <img src={skillsbridgeLogo} alt="SkillsBridge" className="logo-image" />
            SkillsBridge
          </Link>
          <button 
            onClick={handleLogout}
            className="mobile-logout-button"
            aria-label="Log out"
            title="Log Out"
          >
            <LogOut size={20} />
          </button>
        </header>
      )}

      {/* Sidebar for desktop */}
      {!isMobile && (
        <aside className="sidebar">
          <div className="sidebar-header">
            <Link to="/" className="logo">
              {sidebarCollapsed ? (
                <img src={skillsbridgeLogo} alt="SkillsBridge" className="logo-image-collapsed" />
              ) : (
                <>
                  <img src={skillsbridgeLogo} alt="SkillsBridge" className="logo-image" />
                  SkillsBridge
                </>
              )}
            </Link>
            <button 
              className="collapse-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? <Menu size={16} /> : <X size={16} />}
            </button>
          </div>

          <nav>
            <ul>
              <li>
                <Link 
                  to="/admin" 
                  className={activeView === 'dashboard' ? 'active' : ''}
                >
                  <LayoutDashboard size={20} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Dashboard'}</span>
                </Link>
              </li>
              
              <li className="nav-section">
                <span className="section-header">
                  <UserCheck size={20} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Applications & Approvals'}</span>
                </span>
              </li>
              
              <li className="nav-subsection">
                <Link 
                  to="/admin/manage-records/volunteers" 
                  className={activeView === 'volunteer-approvals' ? 'active' : ''}
                >
                  <UserCheck size={18} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Volunteer Approvals'}</span>
                </Link>
              </li>
              
              <li className="nav-subsection">
                <Link 
                  to="/admin/task-approvals" 
                  className={activeView === 'task-approvals' ? 'active' : ''}
                >
                  <ClipboardCheck size={18} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Task Assignments'}</span>
                </Link>
              </li>
              
              <li className="nav-section">
                <span className="section-header">
                  <FolderKanban size={20} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Project Management'}</span>
                </span>
              </li>
              
              <li className="nav-subsection">
                <Link 
                  to="/admin/manage-records/projects" 
                  className={activeView === 'manage-projects' ? 'active' : ''}
                >
                  <FolderKanban size={18} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Manage Projects'}</span>
                </Link>
              </li>
              
              <li className="nav-subsection">
                <Link 
                  to="/admin/manage-skills" 
                  className={activeView === 'manage-skills' ? 'active' : ''}
                >
                  <Settings size={18} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Manage Skills'}</span>
                </Link>
              </li>
              
              <li className="nav-subsection">
                <Link 
                  to="/admin/manage-records/donations" 
                  className={activeView === 'manage-donations' ? 'active' : ''}
                >
                  <DollarSign size={18} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Manage Donations'}</span>
                </Link>
              </li>

              {/* Legacy Data Management Section - can be removed later */}
              <li className="nav-section">
                <span className="section-header">
                  <Database size={20} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Legacy Data Tables'}</span>
                </span>
              </li>
              
              <li className="nav-subsection">
                <Link 
                  to="/admin/manage-records" 
                  className={activeView === 'manage-records' ? 'active' : ''}
                >
                  <Database size={18} />
                  <span className="nav-text">{sidebarCollapsed ? '' : 'Manage Records'}</span>
                </Link>
              </li>
            </ul>
          </nav>
        </aside>
      )}

      {/* Main Content */}
      <main className={`content ${isMobile ? 'mobile-content' : ''}`}>
        <Outlet />
      </main>

      {/* Bottom navigation for mobile */}
      {isMobile && (
        <nav className="bottom-nav">
          <Link 
            to="/admin" 
            className={`nav-item ${activeView === 'dashboard' ? 'active' : ''}`}
          >
            <LayoutDashboard size={20} />
            <span className="nav-label">Dashboard</span>
          </Link>
          <Link 
            to="/admin/manage-records/volunteers" 
            className={`nav-item ${activeView === 'volunteer-approvals' ? 'active' : ''}`}
          >
            <UserCheck size={20} />
            <span className="nav-label">Volunteers</span>
          </Link>
          <Link 
            to="/admin/manage-records/projects" 
            className={`nav-item ${activeView === 'manage-projects' ? 'active' : ''}`}
          >
            <FolderKanban size={20} />
            <span className="nav-label">Projects</span>
          </Link>
          <Link 
            to="/admin/manage-records/donations" 
            className={`nav-item ${activeView === 'manage-donations' ? 'active' : ''}`}
          >
            <DollarSign size={20} />
            <span className="nav-label">Donations</span>
          </Link>
          <Link 
            to="/admin/manage-records" 
            className={`nav-item ${activeView === 'manage-records' ? 'active' : ''}`}
          >
            <Database size={20} />
            <span className="nav-label">Records</span>
          </Link>
        </nav>
      )}
    </div>
  );
}